import { useState } from "react";
import { FormProvider } from "react-hook-form";

import LungReferralImage from "assets/images/lungReferralImage.png";
import ProstateReferralImage from "assets/images/prostateReferral.png";
import { FormDatePicker } from "components/form/FormDatePicker/FormDatePicker";
import { FormField } from "components/form/FormField/FormField";
import { Select } from "components/ui/Select/Select";
import {
  languageOptions,
  narrowedLanguageOptions,
} from "utils/languageOptions";

import { scrollToFirstError } from "../../utils/scrollToFirstError";
import { ItemFormWrapper } from "../ItemFormWrapper/ItemFormWrapper";
import style from "./PatientDetailsForm.module.css";

export const PatientDetailsForm = ({
  onBack,
  onContinue,
  form,
  isLungReferral,
  isProstateReferral,
  practiceOutOfHoursPhone,
}) => {
  const language = form.watch("firstLanguage");

  const [isLanguageExtended, setIsLanguageExtended] = useState(
    narrowedLanguageOptions.find((item) => item.value === language) &&
      !!language
      ? false
      : true
  );

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <>
      {isProstateReferral && (
        <img
          src={ProstateReferralImage}
          alt="National rapid access prostate clinic referral form"
        />
      )}
      {isLungReferral && (
        <img
          src={LungReferralImage}
          alt="National lung cancer rapid access service referral form"
        />
      )}
      <ItemFormWrapper
        onBack={onBack}
        onContinue={form.handleSubmit(handleFormSubmit, scrollToFirstError)}
      >
        <div
          className={style.itemWrapper}
          style={{
            marginBottom: "0px",
            paddingBottom: "0px",
            borderBottom: "none",
          }}
        >
          <FormProvider {...form}>
            <form
              className={style.addReferralForm}
              onSubmit={form.handleSubmit(handleFormSubmit, scrollToFirstError)}
              style={{ gap: "0px" }}
            >
              <div className={style.itemWrapper}>
                <div
                  className={style.inputGroupGrid2Columns}
                  style={{ marginTop: "16px" }}
                >
                  <FormField name="gpOutofHoursNumber">
                    <label
                      className={style.addReferralFormLabel}
                      htmlFor="referral-gp-out-of-hours-number"
                    >
                      General Practitioner Out of Hours Number <span>*</span>
                    </label>
                    {/* With a practice number set, the backend sends it whatever this field says
                        (KIN-5), so the field shows it and does not pretend to be editable. */}
                    <input
                      id="referral-gp-out-of-hours-number"
                      type="text"
                      className={`form-control ${
                        practiceOutOfHoursPhone ? style.readOnlyInput : ""
                      }`}
                      readOnly={!!practiceOutOfHoursPhone}
                      {...form.register("gpOutofHoursNumber")}
                    />
                    {!!practiceOutOfHoursPhone && (
                      <p className={style.fieldHint}>
                        Practice out-of-hours number, change it in Settings →
                        Practice Details
                      </p>
                    )}
                  </FormField>
                </div>
              </div>
              <div className={style.itemWrapper}>
                <div className={style.inputGroupGrid4Columns}>
                  <FormField name="familyName">
                    <label className={style.addReferralFormLabel}>
                      Family Name <span>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("familyName")}
                    />
                  </FormField>
                  <FormField name="firstName">
                    <label className={style.addReferralFormLabel}>
                      First Name <span>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("firstName")}
                    />
                  </FormField>
                  <FormField name="title">
                    <label className={style.addReferralFormLabel}>Title</label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("title")}
                    />
                  </FormField>
                  <FormField name="gender">
                    <label className={style.addReferralFormLabel}>
                      Gender <span>*</span>
                    </label>
                    <Select
                      options={[
                        {
                          label: "Male",
                          value: "MALE",
                        },
                        {
                          label: "Female",
                          value: "FEMALE",
                        },
                      ]}
                      value={form.watch("gender")}
                      onChange={(value) => form.setValue("gender", value)}
                      required
                    />
                  </FormField>
                  <FormField name="dateOfBirth">
                    <label className={style.addReferralFormLabel}>
                      Date of Birth <span>*</span>
                    </label>
                    <div className={style.addPregnancyDatePickerWrapper}>
                      <FormDatePicker form={form} name="dateOfBirth" />
                    </div>
                  </FormField>
                  <FormField name="firstLanguage">
                    <label className={style.addReferralFormLabel}>
                      First Language <span>*</span>
                    </label>
                    <Select
                      options={[
                        ...(isLanguageExtended
                          ? languageOptions
                          : narrowedLanguageOptions),
                      ]}
                      value={form.watch("firstLanguage")}
                      onChange={(value) => {
                        if (value === "oth") {
                          setIsLanguageExtended(true);
                        } else {
                          form.setValue("firstLanguage", value);
                        }
                      }}
                      required
                    />
                  </FormField>
                  <FormField name="wheelchairAssistance">
                    <label className={style.addReferralFormLabel}>
                      Wheelchair Assistance <span>*</span>
                    </label>
                    <Select
                      options={[
                        {
                          label: "Yes",
                          value: "true",
                        },
                        {
                          label: "No",
                          value: "false",
                        },
                      ]}
                      value={form.watch("wheelchairAssistance")}
                      onChange={(value) =>
                        form.setValue("wheelchairAssistance", value)
                      }
                      required
                    />
                  </FormField>
                  <FormField name="interpreterRequired">
                    <label className={style.addReferralFormLabel}>
                      Interpreter Required <span>*</span>
                    </label>
                    <Select
                      options={[
                        {
                          label: "Yes",
                          value: "true",
                        },
                        {
                          label: "No",
                          value: "false",
                        },
                      ]}
                      value={form.watch("interpreterRequired")}
                      onChange={(value) =>
                        form.setValue("interpreterRequired", value)
                      }
                      required
                    />
                  </FormField>
                  <FormField name="mothersMaidenName">
                    <label className={style.addReferralFormLabel}>
                      Mother's Maiden Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("mothersMaidenName")}
                    />
                  </FormField>
                </div>
              </div>
              <div className={style.itemWrapper}>

                <div className={style.inputGroupGrid3Columns}>
                  <FormField name="streetAddress">
                    <label className={style.addReferralFormLabel}>
                      Street Address <span>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("streetAddress")}
                    />
                  </FormField>
                  <FormField name="addressLine2">
                    <label className={style.addReferralFormLabel}>
                      Address Line 2 <span>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("addressLine2")}
                    />
                  </FormField>
                  <FormField name="addressLine3">
                    <label className={style.addReferralFormLabel}>
                      Address Line 3
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("addressLine3")}
                    />
                  </FormField>
                  <FormField name="addressLine4">
                    <label className={style.addReferralFormLabel}>
                      City/Town
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("addressLine4")}
                    />
                  </FormField>
                  <FormField name="eircode">
                    <label className={style.addReferralFormLabel}>
                      Eircode
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("eircode")}
                    />
                  </FormField>
                </div>
              </div>
              <div
                className={style.itemWrapper}
                style={{
                  marginBottom: "0px",
                  paddingBottom: "8px",
                  borderBottom: "none",
                }}
              >
                <div className={style.inputGroupGrid3Columns}>
                  <FormField name="mobile">
                    <label className={style.addReferralFormLabel}>
                      Mobile <span>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("mobile")}
                    />
                  </FormField>
                  <FormField name="phone">
                    <label className={style.addReferralFormLabel}>Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      {...form.register("phone")}
                    />
                  </FormField>
                  <FormField name="email">
                    <label className={style.addReferralFormLabel}>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      {...form.register("email")}
                    />
                  </FormField>
                </div>
              </div>
              <p style={{ color: "#006546" }}>*required inputs</p>
            </form>
          </FormProvider>
        </div>
      </ItemFormWrapper>
    </>
  );
};
